import * as z from 'zod';

import {
  CompanyId,
  EscrowSettingId,
  InvestorId,
  LoanId,
  LoanImportId,
  QcDocumentId,
  RecurringCompanyReportId,
  SftpSiteId,
  SideEffectId,
  UserId,
} from './BrandedIds';
import { CompanyReportFilters, CompanyReportType, ReportFormat } from './CompanyReport';
import { ImportSource, ImportType } from './loan/LoanImport';
import { LoanDashboardColumnKey, LoanDashboardQuery, LoanFilter } from './loanFilter/LoanFilter';
import { LoanTransactionColumnKey, LoanTransactionFilter } from './loanTransactionFilter/LoanTransactionFilter';
import { Form1098Year } from './qcDocument/documents/Form1098';
import { CreateForm1098CorrectionTypeInput } from './qcDocument/QcDocument';
import { QcDocumentFilter } from './qcDocument/QcDocumentFilter';
import { SortBy } from './search/SortBy';
import { TaskFilter } from './tasks';

export interface ProcessFileUploadInput {
  companyId: CompanyId;
  importId: LoanImportId;
  // Provenance only: csv = original Flatfile, csvV2 = new Flatfile, fileImport = Willow
  // internal file import. Execution mode is the separate `enqueued` flag below.
  source: Exclude<ImportSource, 'api'>;
  enqueued: boolean;
}

// API-originated import enqueued as a background job (e.g. custom-template generation).
export type ProcessApiUploadInputEnqueued = {
  companyId: CompanyId;
  importId: LoanImportId;
  source: 'api';
  enqueued: true;
};

// Everything that runs on a queue: file uploads plus API-originated enqueued imports.
export type ProcessQueueUploadInput = ProcessFileUploadInput | ProcessApiUploadInputEnqueued;

// API-originated import processed inline — a REST route awaiting results. `enqueued: false`
// (not the `source` value) selects the return/rethrow behavior in processBatchFile / process* jobs.
export type ProcessApiUploadInputInline = {
  companyId: CompanyId;
  importId: LoanImportId;
  source: 'api';
  enqueued: false;
};

export type ProcessFlatfileUploadInput = ProcessQueueUploadInput & {
  type:
    | 'manualStatement'
    | 'borrowerAchDetails'
    | 'managePointOfContact'
    | 'managePointOfContactSpecialist'
    | 'constructionToPermanent'
    | 'recordPayment'
    | 'accountTransferRevision'
    | 'updateLoan'
    | 'updateEscrowSettings'
    | 'createEscrowSettings'
    | 'createAdvance'
    | 'createLoanNote'
    | 'createLoanFee'
    | 'cancelLoanFee'
    | 'cancelMortgageInsurance'
    | 'failNachaPayments'
    | 'bulkCreatePayees'
    | 'bulkUpdatePayees'
    | 'generateCustomTemplate';
};

export interface SendNewUserEmailInput {
  email: string;
  isSsoUser: boolean;
}

export interface RunSideEffect {
  sideEffectId: SideEffectId;
}

export type ExportDataFile = ExportLoansFile | ExportTransactionsFile | ExportTasksFile | ExportBulkActionFailuresFile;

export type ExportDataFileRecipient =
  | {
      type: 'user';
      id: UserId;
    }
  | {
      type: 'investor';
      id: InvestorId;
    };

export interface ExportLoansFile {
  type: 'loans';
  companyId: CompanyId;
  recipient: ExportDataFileRecipient;
  filters: LoanFilter[];
  sortBy?: SortBy;
  columns: LoanDashboardColumnKey[];
  query: LoanDashboardQuery;
  email?: string;
}

export interface ExportTransactionsFile {
  type: 'transactions';
  companyId: CompanyId;
  recipient: ExportDataFileRecipient;
  filters: LoanTransactionFilter[];
  columns: LoanTransactionColumnKey[];
  query: string;
  email?: string;
}

export interface ExportTasksFile {
  type: 'tasks';
  companyId: CompanyId;
  userId: UserId;
  filters: TaskFilter[];
  query: string;
  sortBy?: SortBy;
  email?: string;
}

export interface ExportBulkActionFailuresFile {
  type: 'bulkActionFailures';
  companyId: CompanyId;
  userId: UserId;
  importType: ImportType;
  startDate: Date;
  endDate: Date;
}

interface RebuildAccountsDashboard {
  companyId: CompanyId;
  type: 'rebuildAccountsDashboard';
}

interface RebuildMetricsDashboard {
  companyId: CompanyId;
  type: 'rebuildMetricsDashboard';
}

interface RebuildTransactionsDashboard {
  companyId: CompanyId;
  type: 'rebuildTransactionsDashboard';
  incrementChangeId: boolean;
}

interface ProvisionDataLake {
  companyId: CompanyId;
  type: 'provisionDataLake';
}

interface DeprovisionDataLake {
  companyId: CompanyId;
  type: 'deprovisionDataLake';
}

interface PurgeAndRebuildDataLake {
  companyId: CompanyId;
  type: 'purgeAndRebuildDataLake';
}

// Re-declares an already-provisioned company's Glue tables to the current exportable
// schema — provisionDataLake no-ops once provisioned (its claim requires provisioned_at IS NULL).
interface UpdateDataLakeSchema {
  companyId: CompanyId;
  type: 'updateDataLakeSchema';
}

export interface CreateCompanyReport {
  type: CompanyReportType;
  emails?: string[];
  sftpSiteId?: SftpSiteId;
  companyId: CompanyId;
  userId: UserId;
  filters: CompanyReportFilters;
  title?: string;
  recurringCompanyReportId?: RecurringCompanyReportId;
  /**
   * Doing two jobs in one field:
   *   1. SQS-retry idempotency key — its presence flips persistence from
   *      "untracked" (row only on success) to "tracked" (skeleton +
   *      heartbeat + stale detection), and dedupes redelivered messages
   *      via `upsertByIdempotencyKey`.
   *   2. Log-correlation ID — the resolver sources this from
   *      `asyncLocalStorage.getStore()?.reqId`, the same reqId pino tags
   *      onto every log line on both resolver and SQS worker sides
   *      (SqsJobHandler re-establishes the reqId in the worker's async
   *      local storage). The frontend Datadog link queries `@reqId:${
   *      idempotencyKey}` to pull the full trace.
   *
   * Both jobs are served by the same UUID today. If either source ever
   * changes independently, the Datadog link silently breaks.
   *
   * Set for one-off SQS reports and re-runs (user wants visibility);
   * unset for cron-fired recurring reports (each tick is independent,
   * delivery is email/SFTP not UI).
   */
  idempotencyKey?: string;
  /**
   * True when this job was kicked off by an explicit user re-run (e.g.
   * `rerunRecurringReportInstance`).
   */
  isRerun?: boolean;
  /**
   * Output format for report types that support both xlsx and csv (currently
   * Transaction History, Trial Balance, Borrower Authorized ACH, Renovo
   * Servicing Transfer Data, Loan Pipeline, and Loans Billed). Undefined for
   * all other report types — they keep their existing fixed format.
   */
  format?: ReportFormat;
}

export type PerformAdminAction =
  | RebuildAccountsDashboard
  | RebuildTransactionsDashboard
  | RebuildMetricsDashboard
  | ProvisionDataLake
  | DeprovisionDataLake
  | PurgeAndRebuildDataLake
  | UpdateDataLakeSchema;

export type GenerateQcDocs =
  | {
      companyId: CompanyId;
      qcDocumentIds: QcDocumentId[];
    }
  | {
      companyId: CompanyId;
      filters: QcDocumentFilter[];
      query: string;
    };

const DraftQcDocsBase = z.object({
  userId: UserId,
  companyId: CompanyId,
  loanIds: LoanId.array().optional(),
});

const Draft1098Docs = DraftQcDocsBase.extend({
  type: z.literal('form1098'),
  year: Form1098Year,
  correctionType: CreateForm1098CorrectionTypeInput,
});

// TODO: Add escrow analysis to this. Make this discriminated union.
export const DraftQcDocs = Draft1098Docs;
export type DraftQcDocs = z.infer<typeof DraftQcDocs>;

const BaseExportMiscFile = z.object({
  userId: UserId,
  companyId: CompanyId,
  email: z.string().optional(),
});

const ExportMiscFileQcDocs = BaseExportMiscFile.extend({
  type: z.literal('qcDocs'),
  filters: QcDocumentFilter.array(),
  query: z.string(),
});

const ExportMiscFileEscrowHistory = BaseExportMiscFile.extend({
  type: z.literal('escrowHistory'),
  loanId: LoanId,
  escrowSettingId: EscrowSettingId,
});

const ExportMiscFile = z.discriminatedUnion('type', [ExportMiscFileQcDocs, ExportMiscFileEscrowHistory]);
export type ExportMiscFile = z.infer<typeof ExportMiscFile>;
